import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Panel, PanelHead } from "./Panel";
import { TextArea } from "./Field";
import { copyText } from "../data/clipboard";
import { fetchAccountUpdates, recordAccountUpdate } from "../data/api";
import { formatDateTime } from "../data/format";
import type { AccountUpdateRecord, AccountUpdatesResponse } from "../data/types";

const button = "rounded-md px-3 py-2 text-sm font-medium transition-colors focus-visible:focus-ring disabled:opacity-50";

function SentUpdate({ u }: { u: AccountUpdateRecord }) {
  const readers = u.seenBy.map((r) => r.name).join(", ");
  return (
    <details className="border-b border-[var(--border)] px-4 py-3 last:border-b-0">
      <summary className="cursor-pointer text-sm">
        <span className="font-medium">{formatDateTime(u.sentAt)}</span>
        <span className="text-[var(--text-2)]"> · {u.sentBy}</span>
        <small className={`block text-xs ${u.seenBy.length > 0 ? "text-success-600" : "text-[var(--text-2)]"}`}>{u.seenBy.length > 0 ? `Seen by ${readers}` : "Not seen yet"}</small>
      </summary>
      <pre className="mt-2 whitespace-pre-wrap font-sans text-xs text-gray-800">{u.text}</pre>
    </details>
  );
}

/**
 * The account's Updates tab: the message to paste into the customer's Slack
 * channel, and the record of those published.
 *
 * The text is editable — what gets copied and published is what is in the
 * box, so a careless edit is published as-is, and that is the point. Copy
 * and Publish are separate: paste first and publish after, or publish
 * without pasting; a mis-click on Copy publishes nothing. Publishing sends
 * the draft's generatedAt as the point the update covers up to, so a change
 * made while the lead was in Slack is not skipped by the next one.
 */
export function AccountUpdates({ accountId }: { accountId: string }) {
  const [data, setData] = useState<AccountUpdatesResponse | null>(null);
  // Apart on purpose: a failed load has nothing to show, a failed publish
  // must keep the edited text on screen with the reason beside the button.
  const [loadError, setLoadError] = useState<string | null>(null);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [text, setText] = useState("");
  const [copied, setCopied] = useState<"ok" | "failed" | null>(null);
  const [busy, setBusy] = useState(false);
  // Bumped after a publish so the draft regenerates from the new anchor.
  const [generation, setGeneration] = useState(0);

  useEffect(() => {
    let live = true;
    fetchAccountUpdates(accountId)
      .then((d) => {
        if (!live) return;
        setData(d);
        setText(d.draft.text);
      })
      .catch(async (e: unknown) => {
        if (live) setLoadError(e instanceof Response ? await e.text() : e instanceof Error ? e.message : "Could not load the update");
      });
    return () => {
      live = false;
    };
  }, [accountId, generation]);

  if (loadError) return <p role="alert" className="text-sm text-error-600">{loadError}</p>;
  if (!data) return <p className="text-sm text-[var(--text-2)]">Loading…</p>;
  const { draft, sent } = data;

  const copy = async () => {
    setCopied((await copyText(text)) ? "ok" : "failed");
    setTimeout(() => setCopied(null), 2500);
  };
  const publish = async () => {
    setBusy(true);
    setPublishError(null);
    try {
      await recordAccountUpdate(accountId, { text: text.trim(), coversUntil: draft.generatedAt });
      setGeneration((g) => g + 1);
    } catch (e) {
      setPublishError(e instanceof Response ? await e.text() : e instanceof Error ? e.message : "Could not publish the update");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_380px]">
      <Panel labelledBy="upd-h">
        <PanelHead id="upd-h" title={`Message for ${draft.account}`} />
        <div className="px-4 py-4">
          <p className="text-xs text-[var(--text-2)]">
            {draft.sinceDefaulted ? "Nothing published yet — the last 14 days" : `Since the last update, ${formatDateTime(draft.since)}`} · generated {formatDateTime(draft.generatedAt)}
          </p>
          {draft.waitingWithoutNote.length > 0 && (
            <p role="alert" className="mt-2 rounded-md border border-warning-400 bg-warning-50 px-3 py-2 text-sm text-warning-700">
              Waiting on the customer with no note:{" "}
              {draft.waitingWithoutNote.map((id, i) => (
                <span key={id}>
                  {i > 0 && ", "}
                  <Link to={`/requests/${id}?from=account`} className="font-medium underline">
                    {id}
                  </Link>
                </span>
              ))}
              . Write the note on the request before sending.
            </p>
          )}
          <TextArea aria-label="Update text" value={text} rows={18} onChange={(e) => setText(e.target.value)} className="mt-3 font-mono text-xs leading-5" />
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button type="button" onClick={() => void copy()} className={`${button} border border-[var(--border-strong)] bg-[var(--bg)] text-gray-700 hover:bg-gray-100`}>
              Copy
            </button>
            <button type="button" onClick={() => void publish()} disabled={busy || text.trim() === ""} className={`${button} bg-brand-600 font-semibold text-white hover:bg-brand-700`}>
              {busy ? "Publishing…" : "Publish to the portal"}
            </button>
            {copied === "ok" && <span className="text-xs text-success-600">Copied</span>}
            {copied === "failed" && <span className="text-xs text-error-600">Copy failed — select the text and copy it</span>}
          </div>
          {publishError && (
            <p role="alert" className="mt-2 text-sm text-error-600">
              {publishError}
            </p>
          )}
        </div>
      </Panel>

      <Panel labelledBy="sent-h">
        <PanelHead id="sent-h" title="Sent updates" count={sent.length} />
        {sent.length === 0 ? (
          <p className="px-4 py-3 text-sm text-[var(--text-2)]">Nothing published yet.</p>
        ) : (
          sent.map((u) => <SentUpdate key={u.id} u={u} />)
        )}
      </Panel>
    </div>
  );
}
